/**
 * Utility functions for parsing and working with tags
 * Supports both structured tag objects and legacy string format (backward compatibility)
 */

import { getCategoryColorSync } from './categories';

/**
 * Check if tags are in structured format
 */
function isStructuredTags(tags) {
  return tags && typeof tags === 'object' && tags.majorCategory !== undefined;
}

/**
 * Check if tags are in old string format
 */
function isStringTags(tags) {
  return typeof tags === 'string' || tags === null || tags === undefined;
}

/**
 * Parse tags - works with both structured and string formats
 * Returns: { majorCategory: string|null, minorTags: string[] }
 */
export function parseTags(tags) {
  if (!tags) return { majorCategory: null, minorTags: [] };
  
  // Structured format
  if (isStructuredTags(tags)) {
    return {
      majorCategory: tags.majorCategory || null,
      minorTags: [...(tags.minorTags || []), ...(tags.customTags || [])]
    };
  }
  
  // Legacy string format
  if (isStringTags(tags)) {
    const parts = (tags || '').split(',').map(t => t.trim()).filter(t => t);
    if (parts.length === 0) return { majorCategory: null, minorTags: [] };
    
    return {
      majorCategory: parts[0],
      minorTags: parts.slice(1)
    };
  }
  
  return { majorCategory: null, minorTags: [] };
}

/**
 * Extract all tags (major + minor + custom) from tags
 * Works with both structured and string formats
 */
export function getAllTags(tags) {
  if (!tags) return [];
  
  // Structured format
  if (isStructuredTags(tags)) {
    const all = [];
    if (tags.majorCategory) all.push(tags.majorCategory);
    if (tags.minorTags) all.push(...tags.minorTags);
    if (tags.customTags) all.push(...tags.customTags);
    return all.filter(Boolean);
  }
  
  // Legacy string format
  if (isStringTags(tags)) {
    const { majorCategory, minorTags } = parseTags(tags);
    return [majorCategory, ...minorTags].filter(Boolean);
  }
  
  return [];
}

/**
 * Get all unique tags from an array of designs
 */
export function getAllUniqueTags(designs) {
  const tagSet = new Set();
  designs.forEach(design => {
    if (design.tags) {
      getAllTags(design.tags).forEach(tag => tagSet.add(tag));
    }
  });
  return Array.from(tagSet).sort();
}

/**
 * Return a case-insensitive alphabetical copy of the provided tag array.
 */
export function sortTagsAlphabetically(tags) {
  if (!Array.isArray(tags)) return [];

  return [...tags]
    .filter(Boolean)
    .sort((a, b) => {
      const normalizedA = a.toLowerCase();
      const normalizedB = b.toLowerCase();
      if (normalizedA === normalizedB) {
        return a.localeCompare(b);
      }
      return normalizedA.localeCompare(normalizedB);
    });
}

/**
 * Get tag counts (how many designs have each tag)
 */
export function getTagCounts(designs) {
  const counts = {};
  designs.forEach(design => {
    if (design.tags) {
      getAllTags(design.tags).forEach(tag => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
    }
  });
  return counts;
}

/**
 * Normalize category name to title case (e.g., "emotions and concepts" -> "Emotions and Concepts")
 */
function normalizeCategoryName(categoryName) {
  if (!categoryName) return null;
  return categoryName
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get major categories with their counts
 * Normalizes category names to match canonical format (title case)
 * Optionally accepts canonicalCategories array to map to exact canonical names
 */
export function getMajorCategoryCounts(designs, canonicalCategories = null) {
  const counts = {};
  
  // Create a case-insensitive map if canonical categories are provided
  const categoryMap = new Map();
  if (canonicalCategories && Array.isArray(canonicalCategories)) {
    canonicalCategories.forEach(cat => {
      if (cat && typeof cat === 'string') {
        categoryMap.set(cat.toLowerCase(), cat);
      }
    });
  }
  
  designs.forEach(design => {
    if (design.tags) {
      const { majorCategory } = parseTags(design.tags);
      if (majorCategory) {
        // Use canonical name if available, otherwise normalize to title case
        const normalized = categoryMap.get(majorCategory.toLowerCase()) 
          || normalizeCategoryName(majorCategory);
        if (normalized) {
          counts[normalized] = (counts[normalized] || 0) + 1;
        }
      }
    }
  });
  return counts;
}

/**
 * Check if a design matches a tag filter
 */
export function designMatchesTag(design, tagFilter) {
  if (!tagFilter) return true;
  if (!design.tags) return false;
  
  const allTags = getAllTags(design.tags);
  return allTags.some(tag => 
    tag && tag.toLowerCase().includes(tagFilter.toLowerCase())
  );
}

/**
 * Get color for major category (for styling)
 * Now uses dynamic categories from API via categories.js
 */
export function getCategoryColor(category) {
  return getCategoryColorSync(category);
}

/**
 * Convert structured tags to string format (for display/backward compatibility)
 */
export function tagsToString(tags) {
  if (!tags) return '';
  
  if (isStructuredTags(tags)) {
    const parts = [];
    if (tags.majorCategory) parts.push(tags.majorCategory);
    if (tags.minorTags) parts.push(...tags.minorTags);
    if (tags.customTags) parts.push(...tags.customTags);
    return parts.join(',');
  }
  
  return tags || '';
}

/**
 * Create structured tags object
 */
export function createStructuredTags(majorCategory, minorTags = [], customTags = []) {
  return {
    majorCategory: majorCategory || null,
    minorTags: Array.isArray(minorTags) ? minorTags : [],
    customTags: Array.isArray(customTags) ? customTags : [],
    metadata: {
      lastUpdated: new Date().toISOString(),
      autoGenerated: false,
      source: 'manual'
    }
  };
}

/**
 * Check if a design is marked as "NOT FOR SALE"
 * Works with both structured and string tag formats
 * Case-insensitive check for "NOT FOR SALE" or "NOT FOR SALE" variations
 */
export function isNotForSale(design) {
  if (!design || !design.tags) return false;
  
  const allTags = getAllTags(design.tags);
  const notForSaleVariations = ['not for sale', 'notforsale', 'not-for-sale'];
  
  return allTags.some(tag => {
    if (!tag) return false;
    const normalizedTag = tag.toLowerCase().trim();
    return notForSaleVariations.some(variation => 
      normalizedTag === variation || normalizedTag.includes(variation)
    );
  });
}

/**
 * Check if a design is marked as "DO NOT DISPLAY"
 * Works with both structured and string tag formats
 * Case-insensitive check for "DO NOT DISPLAY" or variations
 */
export function isDoNotDisplay(design) {
  if (!design || !design.tags) return false;
  
  const allTags = getAllTags(design.tags);
  const doNotDisplayVariations = ['do not display', 'donotdisplay', 'do-not-display'];
  
  return allTags.some(tag => {
    if (!tag) return false;
    const normalizedTag = tag.toLowerCase().trim();
    return doNotDisplayVariations.some(variation => 
      normalizedTag === variation || normalizedTag.includes(variation)
    );
  });
}